import React, { useState, useEffect } from 'react';
import { EditPencil, PlusIcon, ProductCatalogIcon, SpeakerIcon } from '../../../public/icons';
import { useToast } from '../../../context/ToastContext';
import adminFutureGiftService from '../../../services/admin/admin-futureGiftService';
import AddAddonModal from './modal/AddAddonModal';
import adminCompatibleService from '../../../services/admin/admin-addonCompatibleService';
import ConfirmDialog from '../../shared/ConfirmDialog';

const BoxConfigTab = ({ refreshTrigger, onRefresh }) => {
  const { success, error } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    memoryMagnet: { required: true, defaultQty: 1, maxAllowed: 1 },
    emptyFrames: { required: true, defaultQty: 1, maxAllowed: 1 }
  });

  const [customizationRules, setCustomizationRules] = useState({
    uploadOwnPhotos: true,
    chooseCollections: false,
    addMultipleSets: false,
    qrCodeGeneration: false
  });

  const [addons, setAddons] = useState([]);
  const [expandedAddons, setExpandedAddons] = useState({});
  
  // Modal state
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  
  // Confirm Dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // ============ FETCH CONFIGURATION ============
  const fetchFullConfig = async () => {
    try {
      setLoading(true);
      console.log('🔄 [BoxConfigTab] Fetching full configuration...');
      
      const response = await adminFutureGiftService.getFullConfig();
      
      if (response.success) {
        console.log('✅ [BoxConfigTab] Configuration fetched:', response.data);
        
        if (response.data.boxConfig) {
          setConfig(response.data.boxConfig);
        }
        
        if (response.data.customizationRules) {
          setCustomizationRules(response.data.customizationRules);
        }
        
        if (response.data.addons) {
          setAddons(response.data.addons);
        }
      } else {
        error('Failed to load configuration');
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error fetching configuration:', err);
      error('Unable to load configuration. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Load on mount and when refreshTrigger changes
  useEffect(() => {
    fetchFullConfig();
  }, [refreshTrigger]);

  // ============ BOX CONFIGURATION HANDLERS ============
  const handleConfigChange = async (item, field, value) => {
    const updatedConfig = {
      ...config,
      [item]: {
        ...config[item],
        [field]: field === 'required' ? value : parseInt(value) || 0
      }
    };
    
    setConfig(updatedConfig);

    try {
      setSaving(true);
      const response = await adminFutureGiftService.updateBoxConfig(updatedConfig);
      
      if (response.success) {
        success('Box configuration updated successfully');
      } else {
        error('Failed to update configuration');
        fetchFullConfig();
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error updating config:', err);
      error('Failed to update configuration');
      fetchFullConfig();
    } finally {
      setSaving(false);
    }
  };

  // ============ CUSTOMIZATION RULES HANDLERS ============
  const handleRuleToggle = async (rule) => {
    const updatedRules = {
      ...customizationRules,
      [rule]: !customizationRules[rule]
    };
    
    setCustomizationRules(updatedRules);

    try {
      setSaving(true);
      const response = await adminFutureGiftService.updateCustomizationRules(updatedRules);
      
      if (response.success) {
        success('Customization rules updated successfully');
      } else {
        error('Failed to update customization rules');
        fetchFullConfig();
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error updating rules:', err);
      error('Failed to update customization rules');
      fetchFullConfig();
    } finally {
      setSaving(false);
    }
  };

  const handleAddonSubmit = async (addonData) => {
    try {
      setModalLoading(true);
      
      console.log('📤 [BoxConfigTab] Creating add-on:', addonData);

      // Format the addon data for FutureGift schema based on functional status
      const formattedAddon = {
        name: addonData.name,
        enabled: addonData.status === 'active',
        description: addonData.shortDescription || '',
        category: addonData.isFunctional ? 'audio' : 'other',
        isFunctional: addonData.isFunctional,
        sortOrder: addons.length
      };

      if (addonData.isFunctional) {
        // Functional add-on - use items array from variants
        formattedAddon.items = addonData.variants && addonData.variants.length > 0 
          ? addonData.variants.map((variant, index) => ({
              name: variant.name,
              price: variant.price,
              formattedPrice: `₦${variant.price.toLocaleString()}`,
              spec: variant.description || '',
              inventory: variant.stockQuantity || 0,
              image: variant.image || null,
              enabled: true,
              sortOrder: index
            }))
          : [];
      } else {
        // Non-functional add-on - use basePrice
        formattedAddon.basePrice = addonData.basePrice;
        formattedAddon.formattedPrice = `₦${addonData.basePrice.toLocaleString()}`;
      }

      // Include base product info if available
      if (addonData.baseProductInfo) {
        formattedAddon.baseProductId = addonData.baseProductInfo.id;
        formattedAddon.baseProductType = addonData.baseProductInfo.type === 'product' ? 'Product' : 'Collection';
        formattedAddon.baseProductName = addonData.baseProductInfo.name;
      }

      console.log('📤 [BoxConfigTab] Formatted add-on:', formattedAddon);

      let response;
      
      if (editingAddon) {
        response = await adminFutureGiftService.updateAddonGroup(editingAddon._id, formattedAddon);
      } else {
        response = await adminFutureGiftService.createAddonGroup(formattedAddon);
      }
      
      if (response.success) {
        success(editingAddon ? 'Add-on updated successfully!' : 'Add-on created successfully!');
        
        // If base product was selected, associate the add-on with it
        if (addonData.baseProductInfo) {
          try {
            await adminCompatibleService.associateAddon({
              addonId: response.data._id,
              futureGiftId: response.data._id,
              baseProductId: addonData.baseProductInfo.id,
              baseProductType: addonData.baseProductInfo.type,
              addonName: addonData.name,
              price: addonData.basePrice
            });
            console.log('✅ [BoxConfigTab] Add-on associated with base product');
          } catch (assocError) {
            console.error('⚠️ [BoxConfigTab] Failed to associate add-on:', assocError);
          }
        }
        
        setShowAddonModal(false);
        setEditingAddon(null);
        fetchFullConfig();
        if (onRefresh) onRefresh();
      } else {
        error(response.message || 'Failed to save add-on');
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error saving add-on:', err);
      error('Failed to save add-on. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const toggleAddonGroup = async (addonId, currentEnabled) => {
    try {
      setSaving(true);
      const response = await adminFutureGiftService.toggleAddonGroup(addonId, !currentEnabled);
      
      if (response.success) {
        success(`Add-on group ${!currentEnabled ? 'enabled' : 'disabled'} successfully`);
        fetchFullConfig();
      } else {
        error('Failed to toggle add-on group');
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error toggling add-on group:', err);
      error('Failed to toggle add-on group');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (addonId) => {
    setExpandedAddons(prev => ({
      ...prev,
      [addonId]: !prev[addonId]
    }));
  };

  const handleEditAddonItem = async (addonId, itemId, itemData) => {
    try {
      setSaving(true);
      const response = await adminFutureGiftService.updateAddonItem(addonId, itemId, itemData);
      
      if (response.success) {
        success('Add-on item updated successfully');
        fetchFullConfig();
      } else {
        error('Failed to update add-on item');
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error updating add-on item:', err);
      error('Failed to update add-on item');
    } finally {
      setSaving(false);
    }
  };

  const showDeleteAddonItemConfirmation = (addonId, itemId, itemName) => {
    setConfirmAction({
      title: 'Delete Add-on Item',
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: 'Delete Item',
      confirmVariant: 'danger',
      onConfirm: () => handleDeleteAddonItem(addonId, itemId)
    });
    setShowConfirmDialog(true);
  };

  const handleDeleteAddonItem = async (addonId, itemId) => {
    try {
      setSaving(true);
      const response = await adminFutureGiftService.deleteAddonItem(addonId, itemId);
      
      if (response.success) {
        success('Add-on item deleted successfully');
        fetchFullConfig();
      } else {
        error('Failed to delete add-on item');
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error deleting add-on item:', err);
      error('Failed to delete add-on item');
    } finally {
      setSaving(false);
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const handleEditAddonGroup = (addon) => {
    setEditingAddon(addon);
    setShowAddonModal(true);
  };

  const showDeleteAddonGroupConfirmation = (addonId, addonName) => {
    setConfirmAction({
      title: 'Delete Add-on Group',
      message: `Are you sure you want to delete "${addonName}"? This will also delete all items in this group. This action cannot be undone.`,
      confirmText: 'Delete Group',
      confirmVariant: 'danger',
      onConfirm: () => handleDeleteAddonGroup(addonId)
    });
    setShowConfirmDialog(true);
  };

  const handleDeleteAddonGroup = async (addonId) => {
    try {
      setSaving(true);
      const response = await adminFutureGiftService.deleteAddonGroup(addonId);
      
      if (response.success) {
        success('Add-on group deleted successfully');
        fetchFullConfig();
      } else {
        error('Failed to delete add-on group');
      }
    } catch (err) {
      console.error('💥 [BoxConfigTab] Error deleting add-on group:', err);
      error('Failed to delete add-on group');
    } finally {
      setSaving(false);
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const handleModalClose = () => {
    setShowAddonModal(false);
    setEditingAddon(null);
  };

  // ============ FORMATTING HELPERS ============
  const formatPrice = (price) => {
    if (!price) return '₦0';
    return `₦${parseInt(price).toLocaleString()}`;
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'audio':
        return <SpeakerIcon color='#1C2A46' className="w-5 h-5" />;
      default:
        return <ProductCatalogIcon color='#1C2A46' className="w-5 h-5" />;
    }
  };

  // ============ RENDER ============
  if (loading && !config) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55000C]"></div>
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-left relative">
      {/* Auto-save indicator */}
      {saving && (
        <div className="fixed top-4 right-4 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm font-medium">Saving changes...</span>
        </div>
      )}

      {/* Base Items Configuration */}
      <div className="space-y-8 mb-12">
        <div className="bg-white rounded-sm p-5">
          <h2 className="text-xl flex items-center gap-2 font-bold text-[#1C2A46] mb-1"> 
            <ProductCatalogIcon color='#1C2A46' />
            Gift Box Configuration
          </h2>
          <p className="text-[#6B635D] font-light text-sm mb-8">
            Define default physical items and quantity limits for standard boxes.
          </p>
          
          {/* Memory Magnet */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-[#1C2A46]">Memory Magnet</h3>
              <p className="text-sm text-[#6B635D]">Required base item</p>
            </div>
            <p className='bg-[#E7F4ED] text-xs text-[#2F8F5B] font-normal rounded-2xl px-1.5 py-0.5'>
              <span className='h-1.5 w-1.5 bg-[#2F8F5B] rounded-full inline-block mr-2' />
              Required
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-[#6B635D] mb-2">
                Default quantity <span className="ml-1 text-xs text-gray-400">(Auto-saves)</span>
              </label>
              <input
                type="number"
                min="0"
                max={config.memoryMagnet?.maxAllowed || 10}
                value={config.memoryMagnet?.defaultQty || 1}
                onChange={(e) => handleConfigChange('memoryMagnet', 'defaultQty', e.target.value)}
                className="w-full p-3 border border-[#DBDBDB] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:bg-gray-50"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6B635D] mb-2">
                Maximum allowed <span className="ml-1 text-xs text-gray-400">(Auto-saves)</span>
              </label>
              <input
                type="number"
                min="1"
                value={config.memoryMagnet?.maxAllowed || 1}
                onChange={(e) => handleConfigChange('memoryMagnet', 'maxAllowed', e.target.value)}
                className="w-full p-3 border border-[#DBDBDB] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:bg-gray-50"
                disabled={saving}
              />
            </div>
          </div>

          {/* Empty Frames */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-medium text-[#1C2A46]">Empty frame(s)</h3>
              <p className="text-sm text-[#6B635D]">Required base item</p>
            </div>
            <p className='bg-[#E7F4ED] text-xs text-[#2F8F5B] font-normal rounded-2xl px-1.5 py-0.5'>
              <span className='h-1.5 w-1.5 bg-[#2F8F5B] rounded-full inline-block mr-2' />
              Required
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#6B635D] mb-2">
                Default quantity <span className="ml-1 text-xs text-gray-400">(Auto-saves)</span>
              </label>
              <input
                type="number"
                min="0"
                max={config.emptyFrames?.maxAllowed || 10}
                value={config.emptyFrames?.defaultQty || 1}
                onChange={(e) => handleConfigChange('emptyFrames', 'defaultQty', e.target.value)}
                className="w-full p-3 border border-[#DBDBDB] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:bg-gray-50"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#6B635D] mb-2">
                Maximum allowed <span className="ml-1 text-xs text-gray-400">(Auto-saves)</span>
              </label>
              <input
                type="number"
                min="1"
                value={config.emptyFrames?.maxAllowed || 1}
                onChange={(e) => handleConfigChange('emptyFrames', 'maxAllowed', e.target.value)}
                className="w-full p-3 border border-[#DBDBDB] rounded focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent disabled:bg-gray-50"
                disabled={saving}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Customisation Rules */}
      <div className="mb-12 bg-white rounded-sm p-5">
        <h3 className="text-lg font-semibold text-[#1C2A46] mb-1">Customisation Rules</h3>
        <p className="text-[#6B635D] font-light text-sm mb-8">
          Control the workflow for user-generated content and data handling.
          <span className="ml-2 text-xs text-gray-400">(Auto-saves)</span>
        </p>

        <div className="space-y-5">
          {[
            { key: 'uploadOwnPhotos', label: 'Upload Own Photos', description: 'Allow customers to upload custom images for magnets' },
            { key: 'chooseCollections', label: 'Choose Pre-curated collections', description: 'Allow customers to select from pre-made collections' },
            { key: 'addMultipleSets', label: 'Add multiple magnet sets & frames', description: 'Allow purchase of additional magnet set & frames' },
            { key: 'qrCodeGeneration', label: 'QR Code generation', description: 'Automatically generate digital link QR for physical cards.' }
          ].map((rule) => (
            <div key={rule.key} className="flex items-start rounded-lg">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={customizationRules[rule.key]}
                  onChange={() => handleRuleToggle(rule.key)}
                  disabled={saving}
                  className="sr-only peer"
                />
                <div className={`w-6 h-6 border-2 border-[#6B635D] rounded peer-checked:bg-[#1C2A46] peer-checked:border-[#1C2A46] flex items-center justify-center ${
                  saving ? 'opacity-50 cursor-not-allowed' : ''
                }`}>
                  {customizationRules[rule.key] && (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </label>
              <div className="ml-3">
                <p className="font-normal text-base text-[#0E1628]">{rule.label}</p>
                <p className="text-[#6B635D] font-light text-sm mt-1.5">{rule.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add-Ons */}
      <div className='bg-white rounded-sm p-5'>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[#1C2A46] mb-1">Add-Ons</h3>
            <p className="text-[#6B635D] text-sm">Manage extras and secondary gift devices available for purchase.</p>
          </div>
        </div>

        {/* Dynamic Add-ons from API */}
        {addons.map((addonGroup) => (
          <div key={addonGroup._id} className="mb-6 border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-start gap-3">
                {getCategoryIcon(addonGroup.category)}
                <div>
                  <h4 className="font-medium text-[#1C2A46]">{addonGroup.name}</h4>
                  <p className="text-sm text-[#6B635D]">
                    {addonGroup.isFunctional 
                      ? `${addonGroup.items?.length || 0} variants • Functional`
                      : `₦${addonGroup.basePrice?.toLocaleString() || 0} • Standard`
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addonGroup.enabled}
                      onChange={() => toggleAddonGroup(addonGroup._id, addonGroup.enabled)}
                      disabled={saving}
                      className="sr-only peer"
                    />
                    <div className={`w-10 h-5 bg-gray-300 rounded-full peer peer-checked:bg-[#D2A146] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 ${
                      saving ? 'opacity-50 cursor-not-allowed' : ''
                    }`}></div>
                  </label>
                  <span className="text-xs text-[#6B635D]">{addonGroup.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                
                <button
                  onClick={() => handleEditAddonGroup(addonGroup)}
                  className="text-xs text-gray-600 hover:text-gray-800"
                  disabled={saving}
                >
                  Edit
                </button>
                <button
                  onClick={() => showDeleteAddonGroupConfirmation(addonGroup._id, addonGroup.name)}
                  className="text-xs text-red-600 hover:text-red-800"
                  disabled={saving}
                >
                  Delete
                </button>
                
                {addonGroup.isFunctional && addonGroup.items?.length > 0 && (
                  <button 
                    onClick={() => toggleExpand(addonGroup._id)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                  >
                    <svg className={`w-4 h-4 text-[#6B635D] transform transition-transform ${expandedAddons[addonGroup._id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* For functional add-ons, show items when expanded */}
            {addonGroup.isFunctional && expandedAddons[addonGroup._id] && addonGroup.items && addonGroup.items.length > 0 && (
              <div className="mt-4 space-y-3">
                {addonGroup.items.map((item) => (
                  <div key={item._id} className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-[#1C2A46]">{item.name}</h5>
                      <p className="text-sm text-[#6B635D]">
                        {formatPrice(item.price)} • {item.spec || 'No specifications'} • 
                        <span className="ml-1">Inventory: {item.inventory || 0}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEditAddonItem(addonGroup._id, item._id, {
                          name: item.name,
                          price: item.price,
                          spec: item.spec,
                          inventory: item.inventory
                        })}
                        className="text-xs text-[#55000C] hover:text-[#C49660]"
                        disabled={saving}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => showDeleteAddonItemConfirmation(addonGroup._id, item._id, item.name)}
                        className="text-xs text-red-600 hover:text-red-800"
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Empty State */}
        <div className="text-center">
          {addons.length === 0 && (
            <p className="text-gray-500 mb-4">No add-ons configured yet</p>
          )}
          <button 
            onClick={() => {
              setEditingAddon(null);
              setShowAddonModal(true);
            }}
            className="text-sm text-[#55000C] hover:text-[#C49660] font-medium flex items-center justify-center gap-2 mx-auto"
          >
            <PlusIcon className="w-5 h-5" />
            Add your first add-on
          </button>
        </div>
      </div>

      {/* Add Add-on Modal */}
      <AddAddonModal
        isOpen={showAddonModal}
        onClose={handleModalClose}
        onSubmit={handleAddonSubmit}
        loading={modalLoading}
        hideBaseProduct={false}
      />

      {/* Confirm Dialog */}
      {showConfirmDialog && confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          cancelText="Cancel"
          variant={confirmAction.confirmVariant}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => {
            setShowConfirmDialog(false);
            setConfirmAction(null);
          }}
          isOpen={showConfirmDialog}
        />
      )}
    </div>
  );
};

export default BoxConfigTab;